import React, {useCallback, useEffect, useRef, useState} from "react"
import {useNavigate, useParams} from "react-router-dom"
import {useConfig} from "../../hooks/useConfig"
import {useParticipant} from "../../hooks/useParticipant"
import {useBoothSession} from "../../hooks/useBoothSession"
import CameraView from "../views/CameraView"
import CameraControls from "../common/CameraControls"
import CropperModal from "../modals/CropperModal"
import HeadshotTipsModal from "../modals/HeadshotTipsModal"
import SwitchDeviceModal from "../modals/SwitchDeviceModal"
import SizeRejectionModal from "../modals/SizeRejectionModal"
import Spinner from "../common/Spinner"
import {CSSTransition} from "react-transition-group"
import {checkImageSize, cropImage, processImageFile} from "../../utils/ImageUtil"
import {removeBackground, processAiEffect} from "../../library/requests/AiRequests"
import {Area} from "react-easy-crop/types"
import "../../css/Transitions.css"

function CameraPage() {
  const params = useParams<"code">()
  const code = params.code!
  const navigate = useNavigate()

  const {config, loading} = useConfig(code)
  const {participant} = useParticipant(config?.presetCode ?? code)
  const {rawImage, setRawImage, setAiProcessedImage, addAiImage, guestInputValues} = useBoothSession(config?.presetCode ?? code)

  const [cameraOptions, setCameraOptions] = useState<MediaDeviceInfo[]>([])
  const [selectedCameraIndex, setSelectedCameraIndex] = useState(0)
  const [didStartCapture, setDidStartCapture] = useState(false)
  const [finishedCountdown, setFinishedCountdown] = useState(false)
  const [showHeadshotTips, setShowHeadshotTips] = useState(false)
  const [hasShownHeadshotTips, setHasShownHeadshotTips] = useState(false)
  const [showSwitchDevice, setShowSwitchDevice] = useState(false)
  const [showSizeRejection, setShowSizeRejection] = useState(false)
  const [imageToCrop, setImageToCrop] = useState<string | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)

  const sharedCanvas = useRef<HTMLCanvasElement>(null)
  const cameraErrorRef = useRef(cameraError)
  cameraErrorRef.current = cameraError

  useEffect(() => {
    if (config) {
      setShowHeadshotTips(config.headshots.showTips)
    }
  }, [config])

  const handleBackgroundReplacement = useCallback(async (image: string) => {
    if (!config) return
    try {
      const imageUrl = await removeBackground(config.presetCode, image)
      setAiProcessedImage(imageUrl)
    } catch {
      // Navigate to edit even on failure
    }
    navigate(`/${config.presetCode}/edit`)
  }, [config, navigate, setAiProcessedImage])

  const handleAiEffect = useCallback(async (image: string) => {
    if (!config) return
    try {
      const imageUrl = await processAiEffect(config.presetCode, image, guestInputValues)
      addAiImage(imageUrl)
    } catch {
      // Navigate to edit even on failure
    }
    navigate(`/${config.presetCode}/edit`)
  }, [config, navigate, addAiImage, guestInputValues])

  const handleCapturedImage = useCallback((image: string, source: string) => {
    if (!config) return

    checkImageSize(image, config.headshots.minimumSize, (acceptable) => {
      if (acceptable) {
        setRawImage(image)
        setImageToCrop(null)
        setDidStartCapture(false)
        if (config.aiEffectOn) {
          const guestInputs = config.aiEffectSequence?._embedded.guestInputs ?? []
          if (guestInputs.length > 0) {
            navigate(`/${config.presetCode}/guest-input`)
          } else {
            handleAiEffect(image)
          }
        } else if (config.backgroundReplacementOn) {
          handleBackgroundReplacement(image)
        } else {
          navigate(`/${config.presetCode}/edit`)
        }
      } else {
        setShowSizeRejection(true)
        setRawImage(null)
        setDidStartCapture(false)
      }
    })
  }, [config, handleAiEffect, handleBackgroundReplacement, navigate, setRawImage])

  const handleCameraError = useCallback((error: string) => {
    if (cameraErrorRef.current) return
    setCameraError(error)
    if (config) {
      navigate(`/${config.presetCode}/camera-fallback`)
    }
  }, [config, navigate])

  const showImageCropper = useCallback((imageFile: File) => {
    if (!config) return
    processImageFile(imageFile, (imageData: string) => {
      checkImageSize(imageData, config.headshots.minimumSize, (acceptable) => {
        if (acceptable) {
          setImageToCrop(imageData)
        } else {
          setShowSizeRejection(true)
        }
      })
    })
  }, [config])

  const cancelImageCrop = useCallback(() => {
    setImageToCrop(null)
  }, [])

  const finishCrop = useCallback((cropRect: Area, imageData: string) => {
    const image = new Image()
    image.src = imageData

    const canvas = sharedCanvas.current
    if (!canvas) {
      throw new Error("Canvas missing")
    }

    handleCapturedImage(cropImage(image, canvas, cropRect), "camera-fallback")
  }, [handleCapturedImage])

  const switchCamera = useCallback(() => {
    setSelectedCameraIndex(index => {
      const newIndex = index + 1
      return newIndex > cameraOptions.length - 1 ? 0 : newIndex
    })
  }, [cameraOptions.length])

  if (loading || !config) {
    return null
  }

  const theme = config.theme

  return (
    <>
      <canvas className="hiddenCanvas" ref={sharedCanvas}></canvas>

      {imageToCrop &&
          <CropperModal
              show={rawImage == null}
              showFaceGuide={config.headshots.showFaceGuide}
              onClose={cancelImageCrop}
              image={imageToCrop}
              finishCrop={finishCrop}
          />
      }

      <SwitchDeviceModal
        show={showSwitchDevice}
        participant={participant}
        onHide={() => setShowSwitchDevice(false)}
      />

      {cameraOptions.length > 0 && !hasShownHeadshotTips &&
          <HeadshotTipsModal
              show={showHeadshotTips}
              onHide={() => {
                setShowHeadshotTips(false)
                setHasShownHeadshotTips(true)
              }}
          />
      }

      <CameraView
        theme={theme}
        startCapture={didStartCapture}
        selectedCamera={cameraOptions.length > 0 ? cameraOptions[selectedCameraIndex] : null}
        showFaceGuide={config.headshots.showFaceGuide}
        finishCountdown={() => setFinishedCountdown(true)}
        postDelay={(config.aiEffectOn || config.backgroundReplacementOn) ? 0 : 900}
        handleDevices={(devices: MediaDeviceInfo[]) => setCameraOptions(devices)}
        handleImage={(image, source) => handleCapturedImage(image, source)}
        handleError={(error) => handleCameraError(error)}
      />

      <CameraControls
        theme={theme}
        showCameraButton={!didStartCapture && imageToCrop == null && rawImage == null}
        startCapture={() => setDidStartCapture(true)}
        canSwitchCamera={cameraOptions.length > 1}
        switchCamera={() => switchCamera()}
        switchDevice={() => setShowSwitchDevice(true)}
        handleUserSelectedImage={(imageFile) => showImageCropper(imageFile)}
      />

      {showSizeRejection &&
          <SizeRejectionModal
              show={showSizeRejection}
              minWidth={config.headshots.minimumSize.width}
              minHeight={config.headshots.minimumSize.height}
              onHide={() => setShowSizeRejection(false)}
          />
      }

      <CSSTransition in={didStartCapture && !finishedCountdown} timeout={500} exit={false} classNames="fade" unmountOnExit>
        <div className="countdownFade"></div>
      </CSSTransition>

      {(config.aiEffectOn || config.backgroundReplacementOn) && rawImage &&
          <div className="delayedFade mt-5">
            <Spinner color={theme.textStyle.color}/>
          </div>
      }
    </>
  )
}

export default CameraPage
